import { STATIC_LABELS } from "./nav-registry";

/**
 * 案件詳細 (`/admin/cases/$id...`) の「出自セクション」解決。
 *
 * ## 背景 (なぜ必要か)
 * パンくずは route tree (木構造) から導出されるが、案件詳細はツリー上 `全案件管理` の
 * 配下に固定されている。一方で実際の導線はグラフ (DAG) で、案件詳細は
 * 全案件管理 / 承認待ち案件 / 応募管理 / 組織 など **複数の L1 セクションから到達** する。
 * 木は親を1つしか表せないので、放置すると常に「全案件管理」になってしまう。
 *
 * ## 方針
 * 各リンクに出自を手で書かせる (`?from=`) のは O(N) で書き忘れに弱い。代わりに
 * AppLayout が「最後に通った L1 セクション (sidebar entry) のパス」を自動捕捉し、
 * それを出自とみなして **パンくず根とサイドナビ active の両方** をそこへ寄せる。
 * これにより新しい入口を足しても自動で正しくなる。
 */
export interface SectionCrumb {
  label: string;
  to: string;
}

const CANONICAL: SectionCrumb = { label: STATIC_LABELS.adminCases, to: "/admin/cases" };

/**
 * 出自 L1 パス → 案件詳細の根セクション (パンくず根 / サイドナビ active)。
 *
 * - 既知の「案件親 L1」のみ対応付ける。
 * - 組織は companies / members どちらの L1 から来ても「組織一覧」(companies) に寄せる。
 * - それ以外 (ダッシュボード / 不明 / cold load の null) は canonical = 全案件管理 に倒す。
 *
 * @param originL1 AppLayout が捕捉した「最後に訪れた L1 パス」。cold load では null。
 */
export function resolveCaseSection(originL1: string | null | undefined): SectionCrumb {
  switch (originL1) {
    case "/admin/applications":
      return { label: STATIC_LABELS.adminApplications, to: "/admin/applications" };
    case "/admin/organizations/companies":
    case "/admin/organizations/members":
      return {
        label: STATIC_LABELS.adminOrganizations,
        to: "/admin/organizations/companies",
      };
    default:
      return CANONICAL;
  }
}

const ENTERPRISE_CANONICAL: SectionCrumb = {
  label: STATIC_LABELS.enterpriseRoot,
  to: "/enterprise",
};

/**
 * 企業ロールの案件詳細 (`/enterprise/$id...`) の出自セクション解決。
 *
 * 案件詳細はツリー上 `/enterprise` layout の直下に固定だが、実際は
 * ダッシュボード / 案件管理 / 応募管理 から到達する。出自 L1 を根セクションへ寄せる。
 * - 案件管理 / 応募管理 のいずれか
 * - それ以外 (ダッシュボード = 企業の案件一覧 / null) は canonical = ダッシュボードに倒す。
 */
export function resolveEnterpriseSection(
  originL1: string | null | undefined,
): SectionCrumb {
  switch (originL1) {
    case "/enterprise/active":
      return { label: STATIC_LABELS.enterpriseActive, to: "/enterprise/active" };
    case "/enterprise/applications":
      return { label: STATIC_LABELS.enterpriseApplications, to: "/enterprise/applications" };
    default:
      return ENTERPRISE_CANONICAL;
  }
}

const MEMBER_CANONICAL: SectionCrumb = {
  label: STATIC_LABELS.memberRoot,
  to: "/member",
};

/**
 * 会員ロールの案件詳細 (`/member/$id...`) の出自セクション解決。
 *
 * 案件詳細は `/member` layout 直下に固定だが、実際は 案件一覧 / マイ応募 / 企業詳細 から到達する。
 * - マイ応募 / 企業一覧 のいずれか
 * - それ以外 (案件一覧 = 会員トップ / null) は canonical = 案件一覧に倒す。
 * (企業詳細経由の「企業一覧 › 企業名」2階層は buildMemberSectionTrail + originOrg が担う)
 */
export function resolveMemberSection(originL1: string | null | undefined): SectionCrumb {
  switch (originL1) {
    case "/member/my-applications":
      return { label: STATIC_LABELS.memberMyApplications, to: "/member/my-applications" };
    case "/member/companies":
      return { label: STATIC_LABELS.memberCompanies, to: "/member/companies" };
    default:
      return MEMBER_CANONICAL;
  }
}

/** 組織詳細を経由して来た場合の「その組織」コンテキスト。 */
export interface OrgOrigin {
  id: string;
  name: string;
}

/**
 * 案件詳細の祖先 crumb トレイル (pop-last 前) を組み立てる。
 *
 * - 組織詳細を経由した場合 (`originOrg` あり): `組織一覧 › 組織名` の **2階層**。
 *   組織一覧の戻り先タブは出自 L1 (companies / members) を尊重する。
 * - それ以外: 出自 L1 セクション1つ (承認待ち案件 / 応募管理 / 全案件管理…)。
 */
export function buildCaseSectionTrail(
  originL1: string | null | undefined,
  originOrg: OrgOrigin | null | undefined,
): SectionCrumb[] {
  if (originOrg) {
    const tab =
      originL1 === "/admin/organizations/members"
        ? "/admin/organizations/members"
        : "/admin/organizations/companies";
    return [
      { label: STATIC_LABELS.adminOrganizations, to: tab },
      { label: originOrg.name, to: `/admin/organizations/${originOrg.id}` },
    ];
  }
  return [resolveCaseSection(originL1)];
}

/**
 * 会員ロールの案件詳細の祖先 crumb トレイル (pop-last 前)。
 *
 * - 企業詳細を経由した場合 (`originOrg` あり): `企業一覧 › 企業名` の **2階層**。
 * - それ以外: 出自 L1 セクション1つ (案件一覧 / マイ応募 / 企業一覧)。
 */
export function buildMemberSectionTrail(
  originL1: string | null | undefined,
  originOrg: OrgOrigin | null | undefined,
): SectionCrumb[] {
  if (originOrg) {
    return [
      { label: STATIC_LABELS.memberCompanies, to: "/member/companies" },
      { label: originOrg.name, to: `/member/companies/${originOrg.id}` },
    ];
  }
  return [resolveMemberSection(originL1)];
}
